import React from 'react';
import NtfyProvider from './features/contexts/ntfy';
import MainHeaderBar from './components/MainHeaderBar/MainHeaderBar';
import Equipment from './pages/Equipment/Equipment';
import Manifest from './pages/Manifest/Manifest';
import AdasManifest from './pages/AdasManifest/AdasManifest';
import { Routes, Route } from 'react-router-dom';
import RoutingBack from './components/RoutingBack/RoutingsBack';
import Activate from './pages/Activate/Activate';
import SystemInit from './pages/SystemInit/SystemInit';
import SiteSurveys from './pages/SiteSurveys/SiteSurveys';
import Alignments from './pages/Alignments/Alignments';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import Home from './components/Home/Home';
import { Typography, ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { FetchProvider } from './features/contexts/fetch';
import Users from './pages/Users/Users';
import OnlineKeygen from './pages/OnlineKeygen/OnlineKeygen';
import { useState, useMemo, useEffect } from 'react';

// import './App.css';

const pages = [
  {
    name: 'Activate',
    path: '/activate',
    component: Activate,
    secure: false,
    showLink: true,
    needsReadAll: false,
    needsCreateSecurityCodes: false,
    needsManageUsers: false,
  },
  {
    name: 'System Initialization',
    path: '/Kunshan/SystemInit',
    component: SystemInit,
    secure: false,
    showLink: false,
    needsReadAll: false,
    needsCreateSecurityCodes: false,
    needsManageUsers: false,
  },
  {
    name: 'System Initialization',
    path: '/Kunshan/SystemInit.aspx',
    component: SystemInit,
    secure: false,
    showLink: false,
    needsReadAll: false,
    needsCreateSecurityCodes: false,
    needsManageUsers: false,
  },
  {
    name: 'Aligners',
    path: '/aligners',
    component: Equipment,
    secure: true,
    showLink: true,
    needsReadAll: true,
    needsCreateSecurityCodes: false,
    needsManageUsers: false,
  },
  {
    name: 'Alignments',
    path: '/alignments',
    component: Alignments,
    secure: true,
    showLink: true,
    needsReadAll: true,
    needsCreateSecurityCodes: false,
    needsManageUsers: false,
  },
  {
    name: 'Manifest',
    path: '/manifest',
    component: Manifest,
    secure: true,
    showLink: true,
    needsReadAll: true,
    needsCreateSecurityCodes: false,
    needsManageUsers: false,
  },
  {
    name: 'ADAS Manifest',
    path: '/adas_manifest',
    component: AdasManifest,
    secure: true,
    showLink: true,
    needsReadAll: true,
    needsCreateSecurityCodes: false,
    needsManageUsers: false,
  },
  {
    name: 'Codes',
    path: '/codes',
    component: OnlineKeygen,
    secure: true,
    showLink: true,
    needsReadAll: false,
    needsCreateSecurityCodes: true,
    needsManageUsers: false,
  },
  {
    name: 'Users',
    path: '/users',
    component: Users,
    secure: true,
    showLink: true,
    needsReadAll: false,
    needsCreateSecurityCodes: false,
    needsManageUsers: true,
  },
  {
    name: 'Site Surveys',
    path: '/site_surveys',
    component: SiteSurveys,
    secure: true,
    showLink: false,
    needsReadAll: true,
    needsCreateSecurityCodes: false,
    needsManageUsers: false,
  },
];

// @ts-ignore
const ProtectedComponent = ({ component, ...propsForComponent}) => {
  const Cp = withAuthenticationRequired(component);
  return <Cp {...propsForComponent} />
}

function App(): React.JSX.Element {
  const getRouteElement = (page: any) => {
    if (page.secure) {
      return (
        <ProtectedComponent component={page.component} />
      )
    } else {
      return <page.component />;
    }
  }

const [mode, setMode] = useState<'light' | 'dark'>('light');

// Load saved preference on mount
useEffect(() => {
  const saved = localStorage.getItem('themeMode');
  if (saved === 'light' || saved === 'dark') {
    setMode(saved);
  }
}, []);

const theme = useMemo(
  () =>
    createTheme({
      palette: {
        mode,
        ...(mode === 'light'
          ? {
              background: {
                default: '#E6E6E6',
                paper: '#F5F5F5',
              },
            }
          : {
              background: {
                default: '#1A1A1A',
                paper: '#242424',
              },
              text: {
                primary: '#EDEDED',
                secondary: '#B0B0B0',
              },
              primary: { main: '#90CAF9' },
              secondary: { main: '#F48FB1' },
            }),
      },
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            body: {
              transition: 'background-color 0.3s ease-in-out',
            },
            '@keyframes App-logo-spin': {
              from: { transform: 'rotate(0deg)' },
              to: { transform: 'rotate(360deg)' },
            },
            '.App': {
              textAlign: 'center',
            },
            '.App-logo': {
              height: '40vmin',
              pointerEvents: 'none',
              '@media (prefers-reduced-motion: no-preference)': {
                animation: 'App-logo-spin infinite 20s linear',
              },
            },
            '.App-header': {
              minHeight: '100vh',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 'calc(10px + 2vmin)',
            },
            '.App-link': {
              textDecoration: 'none',
            },
          },
        },
      },
    }),
  [mode]
);

// Toggle function
const toggleTheme = () => {
  const next = mode === 'light' ? 'dark' : 'light';
  setMode(next);
  localStorage.setItem('themeMode', next);
};

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <FetchProvider>
        <NtfyProvider>
          <RoutingBack>
            <>
              {/* Pass mode + toggleTheme into your header */}
              <MainHeaderBar
                pages={pages}
                mode={mode}
                toggleTheme={toggleTheme}
              />

              <Routes>
                <Route path="/" element={<Home />} />

                {pages.map((cur, index) => (
                  <Route
                    key={`${cur.name.toLowerCase().replace(' ', '_')}-${index}`}
                    path={cur.path}
                    element={getRouteElement(cur)}
                  />
                ))}
              </Routes>

              <Typography style={{ padding: '0 30px 0 30px' }} variant="caption">
                Aligner Web Portal - Version 1.3.0
              </Typography>
            </>
          </RoutingBack>
        </NtfyProvider>
      </FetchProvider>
    </ThemeProvider>
  );
}

export default App;
